import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Slider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';

import BaseFilterComponent from '@common/react/components/Core/BaseFilterComponent/BaseFilterComponent';
import { sortByStringField } from '@common/react/utils/utils';
import SearchWithCheckboxes from '@common/react/components/UI/SearchWithCheckboxes/SearchWithCheckboxes';

import { Ethnicity } from '@commonTuna/react/objects/Ethnicity';
import { Gender } from '@commonTuna/react/objects/Enums';
import { Page } from '@commonTuna/react/objects/Page';

import { capitalizeFirstLetter } from '@app/components/Utils';
import AvailabilitySwitcherButton from '@app/components/Pages/BeforeAfterList/AvailabilitySwitcherButton';

enum NotAvailableProperties {
	Age = 1,
	Weight = 2,
	Height = 3
}

const NotAvailablePropertiesNames = {
	age: NotAvailableProperties.Age,
	weight: NotAvailableProperties.Weight,
	height: NotAvailableProperties.Height,
};

export const initialSliderFilters = {
	ageMin: 18,
	ageMax: 100,
	weightMin: 80,
	weightMax: 300,
	heightMin: 4,
	heightMax: 7,
	notAvailableProperties: [NotAvailableProperties.Age, NotAvailableProperties.Weight, NotAvailableProperties.Height],
};

export const initialFilterState: Filter = {
	...initialSliderFilters,
	genders: [],
	ethnicities: [],
	pageIds: [],
	indexInCompany: '',
};

export interface Filter {
	ageMin: number;
	ageMax: number;
	weightMin: number;
	weightMax: number;
	heightMin: number;
	heightMax: number;
	genders: Array<Gender>;
	ethnicities: Array<number>;
	indexInCompany: string;
	pageIds?: Array<number>;
	notAvailableProperties?: Array<NotAvailableProperties>;
}

const SliderFilter = (props: {
	filter: Filter;
	min: number;
	max: number;
	field: string;
	title: string;
	changeCallback: (value: Array<number>, field: string) => void;
	submitCallback: (field: string) => void;
	step?: number;
	formatter?: (value: number) => string;
	handleNaChange: (arr: Array<NotAvailableProperties>) => void;
}) => {
	const {
		filter, min, max, field, changeCallback, submitCallback, title, step, formatter, handleNaChange,
	} = props;

	const valueMin = formatter ? formatter(filter[`${field}Min`]) : filter[`${field}Min`];
	const valueMax = formatter ? formatter(filter[`${field}Max`]) : filter[`${field}Max`];

	return <div className="single-filter">
		<div className="single-filter__header">
			<div className="single-filter__title">{title}</div>
			<AvailabilitySwitcherButton
				defaultChecked={(filter.notAvailableProperties || []).some((item) => +item === +NotAvailablePropertiesNames[field])}
				onSwitch={() => {
					let props = filter.notAvailableProperties || [];
					const value = NotAvailablePropertiesNames[field];

					props = props.indexOf(value) === -1 ? [...props, value] : props.filter((g) => g !== value);

					handleNaChange(props.sort());
				}}
			/>
		</div>
		<div className="pull-left">
			Min:
			{valueMin}
		</div>
		<div className="pull-right">
			Max:
			{valueMax}
		</div>
		<br />
		<div className="slider-wrapper">
			<Slider
				range
				min={min}
				max={max}
				step={step || 1}
				value={[filter[`${field}Min`], filter[`${field}Max`]]}
				onInput={(value) => {
					changeCallback(value as Array<number>, field);
				}}
				onThumbDragEnd={() => {
					submitCallback(field);
				}}
			/>
		</div>
	</div>;
};

interface ComponentProps {
	initialValues: any;
}

interface ServiceSelectPage extends Page {
	caseCount: number;
}

type Props = ComponentProps;

const BeforeAfterFilters = (props: Props) => {
	const { t, i18n } = useTranslation();
	const langString: 'Es' | 'En' = capitalizeFirstLetter(i18n.language);

	return (<BaseFilterComponent
		initValues={props.initialValues}
		loadFiltersRequest="caseListBbyFilterRemote"
		render={({
			state: { filters }, actions: { handleChange }, values, setValues,
		}) => {
			const [sliderFilters, setSliderFilters] = React.useState<any>({
				ageMin: filters.ageMin,
				ageMax: filters.ageMax,
				weightMin: filters.weightMin,
				weightMax: filters.weightMax,
				heightMin: filters.heightMin,
				heightMax: filters.heightMax,
				notAvailableProperties: filters.notAvailableProperties,
			});

			const handleSliderChange = (values: Array<number>, field: string) => {
				setSliderFilters((prevState) => ({
					...prevState,
					...filters,
					[`${field}Min`]: values[0],
					[`${field}Max`]: values[1],
				}));
			};

			const handleSliderSubmit = (field: string) => {
				setSliderFilters((prevState) => {
					handleChange({
						[`${field}Min`]: prevState[`${field}Min`],
						[`${field}Max`]: prevState[`${field}Max`],
					});
					return ({
						...prevState,
						...filters,
						[`${field}Min`]: prevState[`${field}Min`],
						[`${field}Max`]: prevState[`${field}Max`],
					});
				});
			};

			const handleNaChange = (arr) => {
				setSliderFilters((prevState) => ({
					...prevState,
					...filters,
					notAvailableProperties: arr,
				}));

				handleChange({
					notAvailableProperties: arr,
				});
			};

			const handleGenderChange = (value: Gender) => {
				let genders = filters.genders;
				genders = genders.indexOf(value) === -1 ? [...genders, value] : genders.filter((g) => g !== value);

				handleChange({ genders });
			};

			const handleEthnicityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
				const id = +event.target.value;
				const ethnicities = filters.ethnicities.indexOf(id) === -1
					? [...filters.ethnicities, id]
					: filters.ethnicities.filter((i) => i !== id);

				handleChange({ ethnicities });
			};

			const getFilters = (): Filter => {
				return {
					ageMin: filters.ageMin,
					ageMax: filters.ageMax,
					weightMin: filters.weightMin,
					weightMax: filters.weightMax,
					heightMin: filters.heightMin,
					heightMax: filters.heightMax,
					notAvailableProperties: filters.notAvailableProperties,
					genders: filters.genders,
					ethnicities: filters.ethnicities,
					pageIds: filters.pageIds,
					indexInCompany: filters.indexInCompany,
				};
			};

			return <>
				<div className="clearfix filters-head">
					<h3 className="pull-left">{t('forms.Filters')}</h3>
					{JSON.stringify(getFilters()) !== JSON.stringify(initialFilterState) && <div
						className="pull-right reset-filters"
						onClick={() => {
							handleChange(initialFilterState);
							setValues((prevValues) => ({
								...prevValues,
								pages: [],
							}));
							setSliderFilters(initialSliderFilters);
						}}
					>
						{t('forms.Reset All')}
						{' '}
						<i className="fa fa-times" />
					</div>}
				</div>
				<SearchWithCheckboxes<ServiceSelectPage>
					titleClassName="single-filter__title"
					className="single-filter"
					title={t('site.Services')}
					type="serviceListRemote"
					loadRequest="serviceSelectListRemote"
					values={filters.pageIds}
					initLoadedAll
					initItems={values?.pages}
					renderName={(item) => `${item.name} (${item.caseCount})`}
					onChange={(values) => {
						handleChange({ pageIds: values?.map((q) => +q) });
					}}
				/>
				<SliderFilter
					filter={sliderFilters}
					min={18}
					max={100}
					field="age"
					title={t('forms.Age')}
					changeCallback={handleSliderChange}
					submitCallback={handleSliderSubmit}
					handleNaChange={handleNaChange}
				/>
				<SliderFilter
					filter={sliderFilters}
					min={80}
					max={300}
					field="weight"
					title={`${t('forms.Weight')} (lb)`}
					changeCallback={handleSliderChange}
					submitCallback={handleSliderSubmit}
					handleNaChange={handleNaChange}
				/>
				<SliderFilter
					filter={sliderFilters}
					min={4}
					max={7}
					step={0.05}
					field="height"
					title={`${t('forms.Height')} (ft)`}
					changeCallback={handleSliderChange}
					submitCallback={handleSliderSubmit}
					formatter={(value) => `${Math.floor(value)}' ${Math.floor((value - Math.floor(value)) * 10) % 12}"`}
					handleNaChange={handleNaChange}
				/>
				<div className="single-filter">
					<div className="single-filter__title">{t('forms.Gender')}</div>
					<div className="single-filter_gender">
						<button
							type="button"
							className={`general-form-button general-form-button_sm ${filters.genders.length
							&& filters.genders.indexOf(Gender.Male) !== -1
							&& 'general-form-button_selected'}`
							}
							onClick={() => { handleGenderChange(Gender.Male); }}
						>
							<i className="fa fa-mars" />
							{' '}
							{t('forms.Male')}
						</button>
						<button
							type="button"
							className={`general-form-button general-form-button_sm ${filters.genders.length
							&& filters.genders.indexOf(Gender.Female) !== -1
							&& 'general-form-button_selected'}`
							}
							onClick={() => { handleGenderChange(Gender.Female); }}
						>
							<i className="fa fa-venus" />
							{' '}
							{t('forms.Female')}
						</button>
						<button
							type="button"
							className={`general-form-button general-form-button_sm ${filters.genders.length
							&& filters.genders.indexOf(Gender.Other) !== -1
							&& 'general-form-button_selected'}`
							}
							onClick={() => { handleGenderChange(Gender.Other); }}
						>
							<i className="fa fa-transgender" />
							{' '}
							{t('forms.Other')}
						</button>
					</div>
				</div>
				<div className="single-filter">
					<div className="single-filter__title">{t('forms.Ethnicity')}</div>
					<div className="single-filter__ethnicities">
						{values?.ethnicities && values.ethnicities.length > 0 && values.ethnicities.sort(sortByStringField(`name${langString}`))
							.map((ethnicity: Ethnicity, index: number) => {
								return <div key={ethnicity.nameEn} className="single-filter__checkbox">
									<label htmlFor={`checkbox-${index}`}>
										<input
											type="checkbox"
											id={`checkbox-${index}`}
											value={ethnicity.id}
											onChange={handleEthnicityChange}
											checked={filters.ethnicities.indexOf(ethnicity.id) !== -1}
										/>
										<span className="fa fa-check">{ethnicity[`name${langString}`]}</span>
									</label>
								</div>;
							})}
					</div>
				</div>
			</>;
		}}
	/>);
};

export default BeforeAfterFilters;
